import React, {useMemo, useRef, useState} from "react";
import {InputGroup, Form, Button, Stack, ListGroup, OverlayTrigger, Tooltip, Modal} from "react-bootstrap";
import {useAlert, useAuth} from "../../../context";
import {
    useSelect,
    useUserCount,
    useUserList,
    useToggle,
    useUserOrderOptions,
    useLocalFilter2,
    useUser,
    useTouch,
    useLongPress
} from "../../../hooks";
import {Link} from "react-router-dom";
import {ResponsiveContent} from "../../ResponsiveNavTabs";
import {
    ItemLoading,
    MenuAdd, MenuBar,
    MenuClearSelectButton,
    MenuConfirmDeleteButton,
    MenuOrderBy,
    MenuSearchTerm,
    OurClearAlert,
    OurPagination2,
    PageTitle
} from "../../_components";

export function Users() {
    return <ResponsiveContent body={<UsersForm/>}/>
}

function UsersForm() {

    const {userId} = useAuth()
    const {success} = useAlert()


    // Get order options
    const {orderOptions, errorOrderOptions, clearErrorOrderOptions} = useUserOrderOptions()

    // API Actions
    const {deleteItem, errorValue, clearErrorValue} = useUser(userId)

    // API count all items
    const {count, setCount, loadingCount, errorCount, clearErrorCount} = useUserCount()

    // API items per page
    const {list, setList, loadingList, errorList, clearErrorList} = useUserList()

    // Filter
    const filteredList = useLocalFilter2(list, ["first_name", "last_name", "username"])

    // Select
    const [selList, toggleSelectItems, clearSelectItems, isItemSelected] = useSelect(list)

    function handleOnItemsDelete() {
        selList
            .forEach(item =>
                deleteItem(item.id)
                    .then(() => {
                        setCount(count => count - 1)
                        setList(prev => prev.filter(obj => obj.id !== item.id))
                        success(`${item.name} has been deleted!`)
                    })
                    .catch(err => console.warn(err.message))
            )
        clearSelectItems()
    }

    return <Stack>

        <OurClearAlert variant="danger" msg={errorOrderOptions} onClear={clearErrorOrderOptions}/>
        <OurClearAlert variant="danger" msg={errorCount} onClear={clearErrorCount}/>
        <OurClearAlert variant="danger" msg={errorList} onClear={clearErrorList}/>
        <OurClearAlert variant="danger" msg={errorValue} onClear={clearErrorValue}/>

        <PageTitle title="User List">Manage {list.length} of {count} users.</PageTitle>


        <MenuBar>
            <MenuAdd to="/backend/user/add" icon="plus">Add</MenuAdd>
            <MenuSearchTerm/>
            <MenuOrderBy orderOptions={orderOptions}/>
            <MenuClearSelectButton hide={selList.length === 0} onClick={clearSelectItems}>
                {selList.length} {selList.length === 1 ? "row" : "rows"}
            </MenuClearSelectButton>
            <MenuConfirmDeleteButton hide={selList.length === 0} onSubmit={handleOnItemsDelete}>
                {selList.length} {selList.length === 1 ? "user" : "users"}
            </MenuConfirmDeleteButton>
        </MenuBar>
        {/*REMEMBER to add edit short*/}

        <ItemLoading show={loadingCount || loadingList}/>

        {list && list.length > 0 ?
            <ListGroup variant="flush">
                {filteredList.map(item =>
                    <RenderItem key={item.id} item={item} selected={isItemSelected(item.id)} selectedCount={selList.length} onSelect={toggleSelectItems}
                                to={`/backend/user/${item.id}`}/>
                )}
            </ListGroup>
            :
            <span className="text-center">No user added yet</span>
        }

        <OurPagination2 loading={loadingCount || loadingList} itemCount={count}/>
    </Stack>
}

function RenderItem({item, selected, selectedCount, onSelect, to}) {
    const touch = useTouch()
    const [show, setShow] = useState(false)

    const ref = useRef(null)
    useLongPress(ref, () => onSelect(item.id))

    // ISO-8601 formatted date returned from server. Eg., 2024-05-21T11:54:10.481649
    const createdDate = useMemo(() => item.created_at ? new Date(item.created_at).toLocaleDateString("en-US") : "---", [item.created_at])
    const createdTime = useMemo(() => item.created_at ? new Date(item.created_at).toLocaleTimeString("en-US") : "", [item.created_at])
    const accessedDate = useMemo(() => item.accesses_at ? new Date(item.accessed_at).toLocaleDateString("en-US") : "---", [item.accessed_at])
    const accessedTime = useMemo(() => item.accesses_at ? new Date(item.accessed_at).toLocaleTimeString("en-US") : "", [item.accessed_at])

    return (
        // <Stack direction="horizontal" gap="2" className={`mb-1 rounded ${selected && "bg-light"} ${show && "bg-light-subtle"}`}
        <Stack direction="horizontal" gap="2" className={`mb-1 rounded ${(selected || show) && "bg-light-subtle"}`}
               onMouseEnter={() => setShow(true)}
               onMouseLeave={() => setShow(false)}>
            <Stack ref={ref} direction="horizontal" className="flex-grow-1" gap="2">
                <div>
                    <UserIcon role={item.role} verified={item.verified} blocked={!item.active}/>
                </div>
                <div>
                    <Stack direction="vertical" className="" gap={0}>
                        <Link to={to}
                              className="flex-shrink-0 link-body-emphasis link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">
                            <span className="text-wrap fw-semibold fs-4">{item.first_name} {item.last_name}</span>
                        </Link>
                        <span className="text-wrap fs-6 fw-light">{item.username}</span>
                    </Stack>
                </div>
                <div className="ms-auto">
                    <Stack className="text-body-tertiary align-items-end justify-content-center" gap={0}>
                        <div><span className="text-nowrap fw-normal fs-6">{createdDate}</span><span className="d-none d-sm-inline"> {createdTime}</span></div>
                        <div><span className="text-nowrap fw-normal fs-6">{accessedDate}</span><span className="d-none d-sm-inline"> {accessedTime}</span></div>
                    </Stack>
                </div>
            </Stack>

            <div className="ms-auto">
                {(!touch || selected || selectedCount > 0) &&
                    <Button className="link-underline link-underline-opacity-0" variant="link" onClick={() => onSelect(item.id)}>
                        <span className={`bi ${selected ? "bi-check-square" : "bi-square"}`}></span>
                    </Button>
                }
            </div>
        </Stack>
    )
}

function UserIcon({role, verified, blocked}) {
    if (blocked)
        return <OverlayTrigger overlay={<Tooltip>Blocked user</Tooltip>}>
            <span><i className="bi bi-person-lock text-danger" style={{fontSize: "2em"}}/></span>
        </OverlayTrigger>
    else if (role === "admin")
        return <OverlayTrigger overlay={<Tooltip>Admin user</Tooltip>}>
            <span><i className="bi bi-person text-info" style={{fontSize: "2em"}}/></span>
        </OverlayTrigger>
    else if (verified)
        return <OverlayTrigger overlay={<Tooltip>Verified user</Tooltip>}>
            <span><i className="bi bi-person-check text-success" style={{fontSize: "2em"}}/></span>
        </OverlayTrigger>
    else
        return <OverlayTrigger overlay={<Tooltip>Not verified yet</Tooltip>}>
            <span><i className="bi bi-person-dash text-warning" style={{fontSize: "2em"}}/></span>
        </OverlayTrigger>
}


function ButtonEditUsers({onSubmit, userList, children}) {

    const [show, setShow] = useState(false)

    const [userRole, setUserRole] = useState(userList[0].role)
    const [verified, setVerified] = useToggle(userList[0].verified)
    const [active, setActive] = useToggle(userList[0].active)

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const handleEdit = () => {
        onSubmit(userList.map(item => item.id), userRole, verified, active)
        handleClose()
    }

    const role = [
        {name: 'User', value: 'user', variant: "primary"},
        {name: 'Admin', value: 'admin', variant: "primary"}
    ];

    const verification = [
        {name: 'Verified', value: true, variant: "primary"},
        {name: 'NotVerified', value: false, variant: "primary"}
    ];

    const blocking = [
        {name: 'Blocked', value: true, variant: "primary"},
        {name: 'NotBlocked', value: false, variant: "primary"}
    ];


    return <>
        <Button variant="primary" className="flex-shrink-0" onClick={handleShow}>{children}</Button>

        <Modal show={show} onHide={handleClose} backdrop="static" keyboard={true} restoreFocus={false}>
            <Modal.Header closeButton>
                <Modal.Title>Fast Edit User</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>{`Your are editing ${userList.length === 1 ? "1 user" : `${userList.length} users`}.`}</p>
                {/*<Form.Check type="switch" id="verifiedSwitch" label="Verified user" checked={verified} onChange={setVerified}/>*/}
                {/*<Form.Check type="switch" id="blockedSwitch" label="Blocked user" checked={blocked} onChange={setBlocked}/>*/}

                <Form.Group sm="6" className="mt-3">
                    <Form.Label column="">Role</Form.Label>
                    <CustomToggle items={role} value={userRole} setValue={setUserRole}/>
                </Form.Group>

                <Form.Group sm="6" className="mt-3">
                    <Form.Label column="">Verify</Form.Label>
                    <CustomToggle items={verification} value={verified} setValue={setVerified}/>
                </Form.Group>

                <Form.Group sm="6" className="mt-3">
                    <Form.Label column="">Block</Form.Label>
                    <CustomToggle items={blocking} value={active} setValue={setActive}/>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>Cancel</Button>
                <Button variant="primary" onClick={handleEdit}>Edit</Button>
            </Modal.Footer>
        </Modal>
    </>
}

function CustomToggle({items, value, setValue}) {
    return <InputGroup>
        {items.map(((item, i) =>
                <Button key={i} variant={`${value === item.value ? "" : "outline-"}${item.variant}`} onClick={() => setValue(item.value)}>
                    {item.name}
                </Button>
        ))}
    </InputGroup>
}
